// Talent availability month (migrated, DESIGN rev 2.1 §2.6 Calendar cell). Two callers: the Production calendar, where
// staff edit a talent's availability on their behalf (talentId), and the performer's own /talent/availability page
// (portal: the Portal profile, §3.3: 44 px targets, 15/22 type, a list of the month's days under the grid; HD-9). The staff
// rendering is unchanged by the portal prop. Same calls: GET /api/talent-availability (?talentId), POST / PATCH / DELETE
// /api/talent-availability (delete takes ?mode=occurrence|future&date= for a weekly entry).
import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react';
import ListRow from '../ui/ListRow';
import api from '../lib/api';
import Button from '../ui/Button';
import CalendarCell from '../ui/CalendarCell';
import Checkbox from '../ui/Checkbox';
import DatePicker from '../ui/DatePicker';
import ErrorState from '../ui/ErrorState';
import Select from '../ui/Select';
import { useConfirm } from '../ui/Confirm';
import Modal from '../ui/Modal';
import { DAY_NAMES, MONTH_NAMES, TIME_OPTIONS, buildCalDays, dateKey, fmtTime12, prettyDate } from '../pages/calendar/util';

const windowLabel = r => (r.availabilityType === 'full_day' ? 'All day' : `${fmtTime12((r.blockStart || '').slice(0, 5))} to ${fmtTime12((r.blockEnd || '').slice(0, 5))}`);

// Mark or edit one day's availability. talentId: staff acting for a talent (POST carries it). strictTimes: Save is
// disabled while the custom window ends before it starts (the Talent calendar's edit mode did this; the view's
// own dialog did not, and the server refuses it either way). portal: the performer's own page (44 px buttons, the payload
// probe's avail-save hook).
export function AvailabilityModal({ dateStr, existing, talentId, strictTimes = false, portal = false, onClose, onSaved, onDeleted }) {
  const confirm = useConfirm();
  const isEdit = Boolean(existing);
  // 'block' entries show as 'custom' so start and end can both be edited
  const [avType,     setAvType]     = useState(() => { const t = existing?.availabilityType || 'full_day'; return t === 'block' ? 'custom' : t; });
  const [blockStart, setBlockStart] = useState(existing?.blockStart || '09:00');
  const [blockEnd,   setBlockEnd]   = useState(() => {
    if (existing?.blockEnd) return existing.blockEnd;
    if (existing?.blockStart) {
      const [h, min] = existing.blockStart.split(':').map(Number);
      return `${String(h + 2).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
    }
    return '11:00';
  });
  const [isRecurring, setIsRecurring] = useState(existing?.isRecurring || false);
  const [recurUntil,  setRecurUntil]  = useState(existing?.recurrenceEndDate ? existing.recurrenceEndDate.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const day = prettyDate(dateStr);
  const badWindow = avType === 'custom' && blockEnd <= blockStart;

  async function handleSave() {
    setSaving(true); setErr('');
    try {
      const hasBlock = avType === 'block' || avType === 'custom';
      const payload = {
        date: dateStr,
        availabilityType: avType,
        blockStart: hasBlock ? blockStart : undefined,
        blockEnd:   avType === 'custom' ? blockEnd : undefined,
        isRecurring,
        recurrenceEndDate: isRecurring && recurUntil ? recurUntil : undefined,
      };
      if (isEdit) await api.patch(`/api/talent-availability/${existing.id}`, payload);
      else        await api.post('/api/talent-availability', talentId ? { ...payload, talentId } : payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'The availability did not save. Try again.'); }
    finally { setSaving(false); }
  }

  async function handleDelete(mode) {
    const ok = await confirm({
      title: mode === 'occurrence' ? 'Remove this date?' : mode === 'future' ? 'Remove this and every later date?' : 'Remove this availability?',
      body: mode === 'occurrence' ? `Remove just ${day}. The other weeks stay.`
        : mode === 'future' ? `Remove ${day} and every later week of this repeating entry.`
        : `Remove the availability on ${day}.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setSaving(true); setErr('');
    try {
      const params = mode === 'occurrence' ? `?mode=occurrence&date=${dateStr}` : mode === 'future' ? `?mode=future&date=${dateStr}` : '';
      await api.delete(`/api/talent-availability/${existing.id}${params}`);
      onDeleted();
    } catch (e) { setErr(e.response?.data?.error || 'The availability was not removed. Try again.'); }
    finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit availability' : 'Mark availability'} meta={day} onClose={onClose}
      footer={(
        <>
          {isEdit && (existing.isRecurring ? (
            <>
              <Button variant="danger" portal={portal} icon={Trash2} onClick={() => handleDelete('occurrence')} disabled={saving}>Remove this date</Button>
              <Button variant="danger" portal={portal} onClick={() => handleDelete('future')} disabled={saving}>Remove all future</Button>
            </>
          ) : (
            <Button variant="danger" portal={portal} icon={Trash2} onClick={() => handleDelete('all')} disabled={saving}>Remove</Button>
          ))}
          <span className="ui-spacer" />
          <Button variant="quiet" portal={portal} onClick={onClose}>Cancel</Button>
          <Button variant="primary" portal={portal} onClick={handleSave} loading={saving} disabled={strictTimes && badWindow}
            data-testid={portal ? 'avail-save' : undefined}>{isEdit ? 'Update' : 'Save'}</Button>
        </>
      )}>
      {err && <p className="ui-p ui-field__error" role="alert">{err}</p>}
      <div className="ui-stack ui-stack--tight">
        <span className="ui-field__label">Availability type</span>
        <div className="ui-seg" role="group" aria-label="Availability type">
          {['full_day', 'custom'].map(v => (
            <Button key={v} variant="quiet" portal={portal} aria-pressed={avType === v} onClick={() => setAvType(v)}>{v === 'full_day' ? 'All day' : 'Custom times'}</Button>
          ))}
        </div>
      </div>
      {avType === 'custom' && (
        <div className="ui-cols ui-cols--tight">
          <Select label="Start time" value={blockStart} onChange={e => setBlockStart(e.target.value)} options={TIME_OPTIONS} />
          <Select label="End time" value={blockEnd} onChange={e => setBlockEnd(e.target.value)} options={TIME_OPTIONS.filter(o => o.value > blockStart)}
            error={badWindow ? 'End time must be after start time.' : undefined} />
        </div>
      )}
      <Checkbox label="Repeat weekly" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} />
      {isRecurring && (
        <div className="ui-cluster">
          <DatePicker label="Repeat until (optional)" hint="Leave blank to repeat indefinitely." value={recurUntil} onChange={e => setRecurUntil(e.target.value)} />
          {recurUntil && <Button variant="quiet" portal={portal} icon={X} onClick={() => setRecurUntil('')}>Clear</Button>}
        </div>
      )}
    </Modal>
  );
}

// The month for one talent (talentId: staff editing on their behalf; omitted: a talent editing their own). portal: the
// performer's own page.
export default function TalentAvailabilityView({ talentId = null, portal = false }) {
  const now = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [rows,  setRows]  = useState([]);
  const [modal, setModal] = useState(null); // null | { dateStr, existing }
  const [loadFailed, setLoadFailed] = useState(false);

  const loadRows = useCallback(async () => {
    try {
      const res = await api.get('/api/talent-availability', talentId ? { params: { talentId } } : undefined);
      setRows(res.data); setLoadFailed(false);
    } catch { setLoadFailed(true); /* an empty month is the fallback; the portal page says so */ }
  }, [talentId]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const days = useMemo(() => buildCalDays(year, month), [year, month]);

  // dateKey → availability rows for the visible month, weekly entries expanded
  const byDate = useMemo(() => {
    const map = {};
    for (const day of days.filter(d => d.getMonth() === month)) {
      const key = dateKey(day);
      const [y2, m2, d2] = key.split('-').map(Number);
      const dow = new Date(Date.UTC(y2, m2 - 1, d2)).getUTCDay();
      const matches = rows.filter(r => {
        const rDate = r.date?.slice(0, 10);
        if (!r.isRecurring) return rDate === key;
        if (rDate > key) return false;
        if (r.recurrenceEndDate && r.recurrenceEndDate.slice(0, 10) < key) return false;
        return r.recurrenceDayOfWeek === dow;
      });
      if (matches.length > 0) map[key] = matches;
    }
    return map;
  }, [rows, days, month]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const todayKey = dateKey(now);
  const openDay = key => setModal({ dateStr: key, existing: (byDate[key] || [])[0] || null });
  const modalEl = modal && (
    <AvailabilityModal dateStr={modal.dateStr} existing={modal.existing} talentId={talentId} portal={portal}
      onClose={() => setModal(null)}
      onSaved={() => { setModal(null); loadRows(); }}
      onDeleted={() => { setModal(null); loadRows(); }} />
  );

  if (portal) {
    const marked = Object.keys(byDate).sort();
    return (
      <>
        <section className="ui-section" aria-label="Month">
          <div className="ui-toolbar">
            <Button variant="quiet" portal iconOnly icon={ChevronLeft} label="Previous month" onClick={prevMonth} data-testid="avail-prev-month" />
            <h2 className="ui-section__title" aria-live="polite">{MONTH_NAMES[month]} {year}</h2>
            <Button variant="quiet" portal iconOnly icon={ChevronRight} label="Next month" onClick={nextMonth} data-testid="avail-next-month" />
          </div>
          {loadFailed && <ErrorState title="Your days did not load" message="The month shows no days for now. Check your connection and try again." onRetry={loadRows} />}
          <div className="ui-month ui-month--portal">
            {DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
            {days.map(day => {
              const key = dateKey(day);
              if (day.getMonth() !== month) return <CalendarCell key={key} date={key} outside />;
              const avRows = byDate[key] || [];
              return (
                <CalendarCell key={key} date={key} today={key === todayKey} max={2} data-testid={`avail-day-${key}`}
                  data-tone={avRows.length ? 'warn' : undefined}
                  items={avRows.map(r => ({ id: r.id, label: `${windowLabel(r)}${r.isRecurring ? ', weekly' : ''}` }))}
                  onClick={() => openDay(key)} />
              );
            })}
          </div>
          <p className="ui-p ui-text-2 ui-cluster ui-cluster--nowrap"><span className="ui-swatch" data-tone="warn" aria-hidden="true" />A marked day is a day you can shoot. Choose any day to add it or change it.</p>
        </section>
        <section className="ui-section" aria-label={`Your days in ${MONTH_NAMES[month]}`}>
          <h2 className="ui-section__title">Your days in {MONTH_NAMES[month]}</h2>
          {marked.length === 0
            ? <p className="ui-p ui-text-2">No days marked in {MONTH_NAMES[month]} yet. Choose a day above to add one.</p>
            : (
              <div className="ui-region ui-list">
                {marked.map(key => (
                  <ListRow key={key} portal title={prettyDate(key)} onClick={() => openDay(key)}
                    meta={byDate[key].map(r => `${windowLabel(r)}${r.isRecurring ? ', every week' : ''}`).join('; ')} />
                ))}
              </div>
            )}
        </section>
        {modalEl}
      </>
    );
  }

  return (
    <div className="ui-stack">
      <div className="ui-toolbar">
        <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={prevMonth} />
        <span className="ui-heading" aria-live="polite">{MONTH_NAMES[month]} {year}</span>
        <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={nextMonth} />
      </div>
      <div className="ui-month">
        {DAY_NAMES.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
        {days.map(day => {
          const key = dateKey(day);
          if (day.getMonth() !== month) return <CalendarCell key={key} date={key} outside />;
          const avRows = byDate[key] || [];
          return (
            <CalendarCell key={key} date={key} today={key === todayKey} max={4}
              data-tone={avRows.length ? 'warn' : undefined}
              items={avRows.map(r => ({ id: r.id, label: `${windowLabel(r)}${r.isRecurring ? ', weekly' : ''}` }))}
              onClick={() => setModal({ dateStr: key, existing: avRows[0] || null })} />
          );
        })}
      </div>
      <p className="ui-p ui-meta ui-cluster"><span className="ui-swatch" data-tone="warn" aria-hidden="true" />Available. Click a day to mark or edit it.</p>
      {modalEl}
    </div>
  );
}
